import React, { useCallback, useMemo } from 'react';
import { Modal, Box, Typography, Form } from '@lucid/lego';
import { useStore } from '../../../store/store';
import { createSendDocusignFormFields } from './formFields';
import styles from './Docusign.module.scss';

/**
 * Form data for the Docusign modal
 */
export type SendDocusignFormData = {
  businessName?: string;
  firstName?: string;
  lastName?: string;
  email: string;
};

/**
 * Initial values for the Docusign form (includes prospectType for conditional rendering)
 */
export type SendDocusignInitialValues = Partial<SendDocusignFormData> & {
  prospectType?: string;
  companyName?: string;
};

/**
 * Props for the Docusign component
 */
export type DocusignProps = {
  /** Test ID for the component */
  dataTestId: string;
  /** Whether the modal is open */
  isOpen: boolean;
  /** Callback when modal is closed */
  onClose: () => void;
  /** Callback when form is submitted */
  onSend: (data: SendDocusignFormData) => void;
  /** Theme variant */
  theme?: 'dark' | 'light';
  /** Initial values for the form */
  initialValues?: SendDocusignInitialValues;
  /** i18n labels */
  labels?: Record<string, string>;
};

/**
 * Docusign modal component for sending Docusign to customer
 * @component
 * @param {DocusignProps} props - The component props
 * @returns {JSX.Element} The Docusign modal
 * @example
 * <Docusign
 *   dataTestId="docusign-modal"
 *   isOpen={isOpen}
 *   onClose={handleClose}
 *   onSend={handleSend}
 *   theme="dark"
 * />
 */
const Docusign: React.FC<DocusignProps> = ({
  dataTestId,
  isOpen,
  onClose,
  onSend,
  theme = 'dark',
  initialValues = {},
  labels,
}) => {
  const locale = useStore((state) => state.locale);
  const language = useStore((state) => state.language);

  // Destructure initialValues for stable memoization dependencies
  const { prospectType, companyName, firstName, lastName, email } = initialValues;

  // Memoize form fields to prevent recreation on every render
  const formFields = useMemo(
    () =>
      createSendDocusignFormFields(
        { prospectType, companyName, firstName, lastName, email },
        { onCancel: onClose },
        labels
      ),
    [prospectType, companyName, firstName, lastName, email, onClose, labels]
  );

  /**
   * Handles successful form submission
   */
  const handleSuccess = useCallback(
    (data: unknown) => {
      const formData = data as SendDocusignFormData;
      onSend({
        businessName: formData.businessName || '',
        firstName: formData.firstName || '',
        lastName: formData.lastName || '',
        email: formData.email || '',
      });
    },
    [onSend]
  );

  return (
    <Modal
      dataTestId={dataTestId}
      isOpen={isOpen}
      onClose={onClose}
      title={labels?.['checkout.docusign.title'] ?? 'Send Docusign'}
      theme={theme}
      size="medium"
      className={styles.modal}
    >
      <Box className={styles.content}>
        <Typography variant="body" size="medium" align="center" theme={theme} color="white">
          {labels?.['checkout.docusign.description'] ??
            'Please confirm the information below to send Docusign to customer and proceed with order.'}
        </Typography>

        <Box className={styles.formFields} mt={24}>
          <Form
            id="send-docusign-form"
            theme={theme}
            formFields={formFields}
            onSuccess={handleSuccess}
            isFluid
            locale={locale}
            language={language ?? locale?.split('-')[0]}
          />
        </Box>
      </Box>
    </Modal>
  );
};

Docusign.displayName = 'Docusign';

export default Docusign;
